// =========================================================================
// Projections for the Workflows section landing. Route `/workflows`.
//
// Demo source: `SECPAGE.workflows` (line 3334) and `renderSectionPage`
// (line 3364). The demo's four stats and ten tiles are reproduced exactly;
// the lineage strip below them is this port's addition, and is described at
// {@link lineageCards}.
//
// Transformation layer — pure functions of an explicit context. No React,
// no data imports beyond the eager seeds, no side effects.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { AREAS, AREA_COVERAGE_LABEL, type Area } from '@/versions/v1/shared/seed/areas';
import { AGENTS } from '@/versions/v1/shared/seed/agents';
import { CONNECTORS } from '@/versions/v1/shared/seed/connectors';
import { MODULES, type ModuleId, type OemModule } from '@/versions/v1/shared/seed/modules';
import { workflowStepsForModule } from '@/versions/v1/shared/seed/workflow-steps';
import type { HeroStat } from './SectionHero';

/** The section's own colour — `SECPAGE.workflows.col`, demo line 3334. */
export const WORKFLOWS_COLOUR = '#A3123F';

/** The section mark. Ten squares in a 3-2-3 stack — the module poster. */
export const WORKFLOWS_ICON =
  '<rect x="3" y="4" width="7" height="6" rx="1.4"/><rect x="14" y="4" width="7" height="6" rx="1.4"/><rect x="3" y="14" width="7" height="6" rx="1.4"/><rect x="14" y="14" width="7" height="6" rx="1.4"/><path d="M10 7h4M10 17h4M6.5 10v4M17.5 10v4"/>';

/** Verbatim from `SECPAGE.workflows.tag`. */
export const WORKFLOWS_TAG = 'ten modules from asset to value';

/** Verbatim from `SECPAGE.workflows.lead()`. */
export const WORKFLOWS_LEAD =
  'The aftermarket operating model, module by module. Every job on the board sits in exactly one of these; each module page shows what it covers, what is open in it now, which agents run it and which systems it reads.';

/** Open jobs per module id, as the caller counts them. */
export type JobsByModule = Readonly<Record<string, number>>;

/**
 * The hero's four figures — `SECPAGE.workflows.stats()`, demo line 3336.
 *
 * `openJobs` is `jobs().length`. It arrives as a number rather than being
 * derived here because the scene already has the job list for the tiles, and
 * counting it twice is how two figures on one page start disagreeing.
 */
export function heroStats(openJobs: number): readonly HeroStat[] {
  return [
    { eyebrow: 'Modules', value: String(MODULES.length), detail: 'asset base to closeout' },
    { eyebrow: 'Open jobs', value: String(openJobs), detail: 'all modules' },
    { eyebrow: 'Agents', value: String(AGENTS.length), detail: 'running the modules' },
    { eyebrow: 'Systems', value: String(CONNECTORS.length), detail: 'connected' },
  ];
}

/** One module tile on the landing grid. */
export interface ModuleTile {
  readonly id: ModuleId;
  /** Poster number, zero-padded — `01` … `10`. */
  readonly n: string;
  /** `'01 Asset Base'`, as the demo composed it. */
  readonly label: string;
  readonly name: string;
  readonly tag: string;
  readonly colour: string;
  readonly icon: string;
  readonly agent: string;
  /** Open jobs, or `'—'` for none. The demo's `q.length || '—'`. */
  readonly count: string;
  /** How many workflow steps the module carries. */
  readonly steps: number;
}

/**
 * The ten tiles — `SECPAGE.workflows.tiles()`, demo line 3337.
 *
 * The demo's tile carried `[id, label, tag, count, handler, icon, colour]`.
 * `count` is `q.length || '—'`, which prints an em dash for a module with
 * nothing waiting — kept, because "0" and "nothing here" read differently in
 * a grid of ten.
 *
 * `agent` and `steps` are not on the demo's tile. They are the two things a
 * reader choosing between ten modules actually wants, and both are already in
 * the seed, so the tile says them rather than making the reader open all ten.
 */
export function moduleTiles(jobsByModule: JobsByModule): readonly ModuleTile[] {
  return MODULES.map((m: OemModule): ModuleTile => {
    const open = jobsByModule[m.id] ?? 0;
    return {
      id: m.id,
      n: String(m.n).padStart(2, '0'),
      label: `${String(m.n).padStart(2, '0')} ${m.name}`,
      name: m.name,
      tag: m.tag,
      colour: m.col,
      icon: m.icon,
      agent: m.agent,
      count: open > 0 ? String(open) : '—',
      steps: workflowStepsForModule(m.id).length,
    };
  });
}

/** One capability card in the lineage strip. */
export interface LineageCard {
  readonly id: string;
  /** Wheel number, zero-padded. */
  readonly n: string;
  readonly name: string;
  readonly isNew: boolean;
  /** What the original programme promised. */
  readonly then: string;
  /** What it is in the workspace now. */
  readonly now: string;
  /** `'Asset & work'` or `'Service operations'`. */
  readonly coverage: string;
  readonly coverageKey: Area['cov'];
  readonly agent: string;
  /** The modules this capability lives in, by id. */
  readonly modules: readonly ModuleId[];
  readonly links: Area['links'];
  readonly stat: Area['stat'];
}

/**
 * All fourteen capabilities of the original aftermarket wheel.
 *
 * The demo showed the lineage strip only on a module page, filtered to that
 * module's `caps`. Both are true and neither replaces the other: the module
 * page answers "what did this module absorb", and this answers "where did the
 * whole programme go" — which is the question the section landing is for, and
 * the one a reader coming from the operating-model poster arrives with.
 *
 * `modules` is the reverse of `OemModule.caps`, computed here so a card can
 * say where it went.
 */
export function lineageCards(): readonly LineageCard[] {
  const owners = new Map<string, ModuleId[]>();
  for (const m of MODULES) {
    for (const cap of m.caps) {
      const list = owners.get(cap);
      if (list) list.push(m.id);
      else owners.set(cap, [m.id]);
    }
  }

  return AREAS.map(
    (a: Area): LineageCard => ({
      id: a.id,
      n: String(a.n).padStart(2, '0'),
      name: a.name,
      isNew: a.isNew === true,
      then: a.then,
      now: a.now,
      coverage: AREA_COVERAGE_LABEL[a.cov],
      coverageKey: a.cov,
      agent: a.agent,
      modules: owners.get(a.id) ?? [],
      links: a.links,
      stat: a.stat,
    }),
  );
}

/** Module id → its poster number and name, for a lineage card's "lives in". */
export const MODULE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  MODULES.map((m) => [m.id, `${String(m.n).padStart(2, '0')} ${m.short}`]),
);
